/*
 * LockPrompt.jsx
 * --------------
 * Shown to guest users when they attempt to access a feature
 * that requires an authenticated account.
 * Can render as an inline block or a modal overlay.
 * Accepts a feature name and optional description to customize the message.
 */

import React, { useEffect } from "react";

// ============================================
// LOCK ICON
// ============================================
function LockIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <rect
        x="6" y="14" width="20" height="14" rx="3"
        stroke="var(--accent-gold)" strokeWidth="1.5"
      />
      <path
        d="M10 14V10C10 7.24 12.24 5 15 5H17C19.76 5 22 7.24 22 10V14"
        stroke="var(--accent-gold)" strokeWidth="1.5" strokeLinecap="round"
      />
      <circle cx="16" cy="21" r="2" fill="var(--accent-gold)" />
      <path
        d="M16 21V24"
        stroke="var(--accent-gold)" strokeWidth="1.5" strokeLinecap="round"
      />
    </svg>
  );
}

// ============================================
// INLINE VARIANT
// Renders as a block inside the page layout
// ============================================
function InlineLockPrompt({ feature, description, onSignIn }) {
  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderRadius: "var(--radius-md)",
      padding: "var(--space-6)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "var(--space-3)",
      textAlign: "center",
      position: "relative",
      overflow: "hidden"
    }}>

      {/* Gold top border accent */}
      <div style={{
        position: "absolute",
        top: 0, left: 0, right: 0,
        height: "3px",
        background: "linear-gradient(90deg, var(--accent-green), var(--accent-gold))"
      }} />

      <LockIcon />

      <div>
        <h3 style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "18px",
          color: "var(--text-primary)",
          margin: "0 0 var(--space-2)"
        }}>
          {feature} is a signed-in feature
        </h3>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "14px",
          color: "var(--text-secondary)",
          margin: 0,
          lineHeight: 1.6
        }}>
          {description || "Sign in with your Microsoft account to unlock this feature and keep your data safe across sessions."}
        </p>
      </div>

      <button
        onClick={onSignIn}
        style={{
          background: "var(--accent-green)",
          border: "none",
          borderRadius: "var(--radius-sm)",
          padding: "var(--space-3) var(--space-6)",
          cursor: "pointer",
          color: "white",
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: "15px",
          marginTop: "var(--space-2)",
          transition: "opacity var(--transition-fast)",
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)"
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
        onMouseLeave={e => e.currentTarget.style.opacity = "1"}
      >
        {/* Microsoft logo */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <rect x="1" y="1" width="6.5" height="6.5" fill="#f25022"/>
          <rect x="8.5" y="1" width="6.5" height="6.5" fill="#7fba00"/>
          <rect x="1" y="8.5" width="6.5" height="6.5" fill="#00a4ef"/>
          <rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#ffb900"/>
        </svg>
        Sign in with Microsoft
      </button>

      <p style={{
        fontFamily: "var(--font-body)",
        fontSize: "12px",
        color: "var(--text-muted)",
        margin: 0
      }}>
        Free to use with any Microsoft account
      </p>
    </div>
  );
}

// ============================================
// MODAL VARIANT
// Renders as a centered overlay modal
// ============================================
function ModalLockPrompt({ feature, description, onSignIn, onClose }) {

  // Close on Escape
  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape" && onClose) onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1200,
          animation: "fadeIn 150ms ease"
        }}
      />

      {/* Modal */}
      <div style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "min(420px, calc(100vw - 32px))",
        background: "var(--bg-surface)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-lg)",
        zIndex: 1201,
        overflow: "hidden",
        animation: "fadeIn 200ms ease"
      }}>

        {/* Gold top accent */}
        <div style={{
          height: "4px",
          background: "linear-gradient(90deg, var(--accent-green), var(--accent-gold))"
        }} />

        <div style={{
          padding: "var(--space-6)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-3)",
          textAlign: "center"
        }}>

          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: "var(--space-4)",
                right: "var(--space-4)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--text-muted)",
                padding: "var(--space-1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "var(--radius-sm)",
                transition: "color var(--transition-fast)"
              }}
              onMouseEnter={e => e.currentTarget.style.color = "var(--text-primary)"}
              onMouseLeave={e => e.currentTarget.style.color = "var(--text-muted)"}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          )}

          <LockIcon />

          <div>
            <h3 style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: "20px",
              color: "var(--text-primary)",
              margin: "0 0 var(--space-2)"
            }}>
              {feature} is a signed-in feature
            </h3>
            <p style={{
              fontFamily: "var(--font-body)",
              fontSize: "14px",
              color: "var(--text-secondary)",
              margin: 0,
              lineHeight: 1.6
            }}>
              {description || "Sign in with your Microsoft account to unlock this feature and keep your data safe across sessions."}
            </p>
          </div>

          <button
            onClick={onSignIn}
            style={{
              background: "var(--accent-green)",
              border: "none",
              borderRadius: "var(--radius-sm)",
              padding: "var(--space-3) var(--space-6)",
              cursor: "pointer",
              color: "white",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "15px",
              marginTop: "var(--space-2)",
              transition: "opacity var(--transition-fast)",
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
              width: "100%",
              justifyContent: "center"
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect x="1" y="1" width="6.5" height="6.5" fill="#f25022"/>
              <rect x="8.5" y="1" width="6.5" height="6.5" fill="#7fba00"/>
              <rect x="1" y="8.5" width="6.5" height="6.5" fill="#00a4ef"/>
              <rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </button>

          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            color: "var(--text-muted)",
            margin: 0
          }}>
            Free to use with any Microsoft account
          </p>
        </div>
      </div>
    </>
  );
}

// ============================================
// MAIN EXPORT
// Decides which variant to render based on props
// ============================================
export default function LockPrompt({ feature, description, variant = "inline", onClose }) {

  function handleSignIn() {
    window.location.href = "/.auth/login/aad";
  }

  if (variant === "modal") {
    return (
      <ModalLockPrompt
        feature={feature}
        description={description}
        onSignIn={handleSignIn}
        onClose={onClose}
      />
    );
  }

  return (
    <InlineLockPrompt
      feature={feature}
      description={description}
      onSignIn={handleSignIn}
    />
  );
}